// #697: one wall-clock grid for every steady-state background poller.
//
// WHY THIS EXISTS (owner ask, 2026-07-28): "it should be offset from when health and
// notifications polls run". Before this, the three long-lived pollers each ran a timer
// armed at its own mount moment:
//
//   heartbeat      /health                      15s   (setTimeout chain — drifts by
//                                                      request latency, so its phase
//                                                      wanders and cannot be reasoned
//                                                      about at all)
//   notifications  /notifications/unseen-count  30s   (setInterval)
//   reviewActivity the review change probe      10s   (setInterval)
//
// 10 / 15 / 30 share an LCM of 30s, so all three coincided twice a minute — three
// requests landing in the same tick on a backend whose free-tier instance serialises
// them, on top of the per-project fan-out this change also removes. Mount-time phase
// made that unfixable by construction: two pollers mounted in the same render land in
// the same millisecond and stay there forever.
//
// The fix is to stop deriving phase from mount time. Every poller ticks when the WALL
// CLOCK satisfies `now ≡ offsetMs (mod intervalMs)`, so the phases are a property of
// the schedule below rather than of when a component happened to render, and
// `assertDisjoint` can prove — over a whole LCM window, not by inspection — that no
// two ever collide. A schedule change that reintroduces a collision fails a test.
//
// A second benefit falls out: re-arming against the clock rather than `interval + now`
// makes each tick self-correcting, so a slow request cannot make the cadence drift.
//
// KNOWN TRADE, stated rather than discovered later: a shared grid means several TABS
// of the same user fire in the same instant instead of being spread by mount time. It
// is the right trade here — hidden tabs stand down entirely (see useReviewActivity),
// and this change cuts a review-activity tick from O(projects) requests to one, so the
// total is far below where it started either way.
//
// FOLLOW-UP (measured, not just theorised): that trade was real. A local repro with
// REAL browser tabs driving this real code (e2e/tests/oom-697-synchronized-poll-
// burst.spec.ts) measured +14.5MB RSS @ 20 concurrent tabs and +31.4MB @ 50, mostly
// NOT glibc-arena-recoverable (a few MB of malloc_trim recovery against tens of MB
// retained) — every tab in the world was computing the identical next-tick instant
// off the Unix epoch, so N tabs produced a synchronized burst instead of N requests
// spread across the interval.
//
// Fix: anchor `now` to THIS TAB's own load time (`TAB_EPOCH_MS` below) instead of the
// Unix epoch, reintroducing the staggering that existed, implicitly, before this
// change (different tabs load at different real moments) — for free, no new state or
// randomness. The intra-tab disjointness proof (`ticksWithinCycle`,
// pollSchedule.test.js) is UNTOUCHED by this: it is a property of the offsets' pairwise
// differences, which an additive shift to `now` cannot change, whatever that shift is.
//
// This does NOT fully replace jitter: tabs that load within the same instant (a
// browser restoring many saved tabs, a mass reload right after a deploy) still
// compute nearly the same TAB_EPOCH_MS and so still collide — if that correlated-
// mass-mount case turns out to matter in practice, bounded jitter can still be
// layered on top without touching this or the disjointness proof.
const TAB_EPOCH_MS = Date.now();

export const POLL_SCHEDULE = {
  // Offsets are chosen so the tick sets are disjoint across the 30s LCM — see
  // assertDisjoint below, which is what actually holds this true.
  heartbeat: { intervalMs: 15000, offsetMs: 0 },
  notifications: { intervalMs: 30000, offsetMs: 5000 },
  reviewActivity: { intervalMs: 10000, offsetMs: 3000 },
};

/**
 * Milliseconds from `now` until the next instant satisfying `t ≡ offsetMs (mod
 * intervalMs)`. Never returns 0 — landing exactly on a grid point means the next tick
 * is a full interval away, not immediately (a 0 would busy-loop a setTimeout chain).
 *
 * `now`'s default is tab-epoch-relative (`Date.now() - TAB_EPOCH_MS`), not the raw
 * Unix clock — see the module comment above. Every test passes its own `now`
 * explicitly, so this default only affects real (un-mocked) callers.
 */
export function msUntilNextTick(intervalMs, offsetMs = 0, now = Date.now() - TAB_EPOCH_MS) {
  const phase = (((now - offsetMs) % intervalMs) + intervalMs) % intervalMs;
  const delay = intervalMs - phase;
  return delay === 0 ? intervalMs : delay;
}

/**
 * Arm `fn` on the named slot's grid. Returns a cancel function.
 *
 * Re-arms from the clock after every tick rather than from `now + interval`, so a slow
 * or hung `fn` cannot walk the poller off its slot and into another one's.
 *
 * SELF-PACING (measured follow-up, not just theorised): if `fn` returns a promise, the
 * next tick is armed only once it SETTLES (success or failure) — not immediately after
 * `fn` is merely CALLED. Before this, an async `fn` was fire-and-forgotten: `arm()` ran
 * in the same synchronous tick as calling `fn`, so a slow backend never delayed the
 * next request at all — the poller kept firing on schedule regardless of how many of
 * its own previous calls were still in flight. Under a CPU-starved backend (Render's
 * 0.1 vCPU free tier — a request that normally takes ~50ms can take multiple SECONDS)
 * this is exactly backwards: a local repro with real browser tabs measured +152MB peak
 * RSS @ 100 tabs under CPU throttling (crossing the 512MB ceiling) vs +47-49MB at full
 * CPU speed with the SAME code — because slow responses pile up concurrently instead of
 * naturally spacing out the next request. A sync `fn` (tests; nothing production calls
 * this way) re-arms exactly as before — this only changes behaviour for a `fn` that
 * returns a thenable.
 *
 * `minGapMs`: a floor under `msUntilNextTick`'s answer, applied AFTER `fn` settles. Grid
 * alignment alone can occasionally land the next tick almost immediately (a slow
 * response that finishes just before its own next grid point) — the floor guarantees a
 * real gap after every response regardless of where that lands on the grid.
 *
 * JITTER (measured follow-up, not just theorised — #737 tab-count ceiling retest):
 * TAB_EPOCH_MS staggers tabs that load at genuinely different moments, but tabs opened
 * in a tight burst (many tabs restored/opened within the same few seconds — measured
 * with 50 real tabs against a real multi-project account) still compute nearly the same
 * epoch and so still collide on the SAME grid tick, all at once, exactly as this file's
 * module comment predicted. A 50-real-tab CPU-throttled repro hit the identical
 * `QueuePool` exhaustion #737 already root-caused, `error_class: TimeoutError`, because
 * enough of those 50 independent batched requests landed in one tick to exceed the
 * 10-connection pool even though each individual tab's poll is already batched to one
 * request. `jitterMs` adds a small POSITIVE-only random offset to each rearm — bounded
 * well under the 2000ms minimum gap between this schedule's ticks (see
 * `ticksWithinCycle`) so it cannot walk a poller into a neighbouring slot's tick — which
 * spreads a same-instant burst across a window instead of leaving every tab computing
 * the identical delay.
 */
export function scheduleAligned(slot, fn,
  { now = () => Date.now() - TAB_EPOCH_MS, setTimer, clearTimer, minGapMs = 0,
    jitterMs = 400, random = Math.random } = {}) {
  const cfg = POLL_SCHEDULE[slot];
  if (!cfg) throw new Error(`unknown poll slot: ${slot}`);
  const set = setTimer || ((f, d) => setTimeout(f, d));
  const clear = clearTimer || ((t) => clearTimeout(t));
  let timer = null;
  let cancelled = false;
  const rearm = () => {
    if (cancelled) return;
    const jitter = jitterMs > 0 ? random() * jitterMs : 0;
    const delay = Math.max(msUntilNextTick(cfg.intervalMs, cfg.offsetMs, now()), minGapMs) + jitter;
    timer = set(() => {
      if (cancelled) return;
      const result = fn();
      if (result && typeof result.then === 'function') {
        result.then(rearm, rearm);
      } else {
        rearm();
      }
    }, delay);
  };
  rearm();
  return () => { cancelled = true; if (timer) clear(timer); };
}

/**
 * Every tick offset a slot produces within one full cycle of the schedule.
 *
 * Exists for the test that proves the offsets are actually disjoint. Asserting the
 * schedule by reading it is how the original collision survived: 10/15/30 look
 * unrelated until you compute the LCM.
 */
export function ticksWithinCycle(slot, cycleMs = 30000) {
  const { intervalMs, offsetMs } = POLL_SCHEDULE[slot];
  const out = [];
  for (let t = offsetMs % intervalMs; t < cycleMs; t += intervalMs) out.push(t);
  return out;
}
